import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// Create axios instance for external API calls
const externalApi = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
  withCredentials: false,
});

// Add response interceptor for better error handling
externalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please check your internet connection.');
    }
    if (error.response?.status === 0) {
      throw new Error('Network error. Please check if the API server is accessible.');
    }
    if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Please check the URL.');
    }
    if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    }
    throw error;
  }
);

export const GetAllPagesWithSEO = () => {
  return useQuery({
    queryKey: ["pages-seo"],
    queryFn: async () => {
      try {
        console.log("Fetching pages from:", "https://aapharmasyn.com/admin/api/seo/pages");

        // Try different API endpoint variations
        let response;

        // First try the main endpoint
        try {
          response = await externalApi.get("https://aapharmasyn.com/admin/api/seo/pages");
          console.log("Main API endpoint successful:", response);
        } catch (apiError) {
          console.log("Main API endpoint failed:", apiError.message);

          // Try alternative endpoint structure
          try {
            response = await externalApi.get("https://aapharmasyn.com/api/seo/pages");
            console.log("Alternative API endpoint successful:", response);
          } catch (altError) {
            console.log("Alternative API endpoint failed:", altError.message);
            throw new Error(`Failed to connect to API: ${altError.message}`);
          }
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching pages:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers
          }
        });
        throw error;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
};

// Get SEO data for a specific page
export const GetPageSEO = (pageId) => {
  return useQuery({
    queryKey: ["page-seo", pageId],
    queryFn: async () => {
      try {
        console.log("Fetching SEO data for page:", pageId);
        
        let response;
        
        // Try different API endpoint variations
        try {
          console.log("Trying endpoint: https://aapharmasyn.com/admin/api/seo/pages/" + pageId);
          response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/pages/${pageId}`);
          console.log("SEO fetch successful:", response.data);
        } catch (apiError) {
          console.log("Main API endpoint failed:", apiError.message);
          
          try {
            console.log("Trying alternative endpoint: https://aapharmasyn.com/api/seo/pages/" + pageId);
            response = await externalApi.get(`https://aapharmasyn.com/api/seo/pages/${pageId}`);
            console.log("Alternative API endpoint successful:", response.data);
          } catch (altError) {
            console.log("Alternative API endpoint failed:", altError.message);
            
            // Try one more endpoint variation
            try {
              console.log("Trying third endpoint: https://aapharmasyn.com/admin/api/seo/page/" + pageId);
              response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/page/${pageId}`);
              console.log("Third API endpoint successful:", response.data);
            } catch (thirdError) {
              console.log("Third API endpoint failed:", thirdError.message);
              throw new Error(`Failed to fetch SEO data. Tried multiple endpoints. Last error: ${thirdError.message}`);
            }
          }
        }
        
        // If no SEO data exists, return empty structure
        if (!response.data || !response.data.data) {
          return {
            data: {
              seo_title: "",
              meta_description: "",
              keywords: "",
              og_title: "",
              og_description: "",
              og_image: "",
              canonical_url: "",
              meta_robots: "index,follow",
              structured_data: ""
            }
          };
        }
        
        return response.data;
      } catch (error) {
        console.error("Error fetching SEO data:", error);
        throw error;
      }
    },
    enabled: !!pageId,
  });
};

// Create or update SEO data for a page
export const SavePageSEO = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (seoData) => {
      try {
        console.log("Saving SEO data:", seoData);
        console.log("Page ID:", seoData.page_id);
        
        // Try different API endpoint variations
        let response;
        
        // Determine if payload is FormData (file upload) or JSON
        const isForm = typeof FormData !== 'undefined' && seoData instanceof FormData;

        const postOptions = isForm ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

        // First try the main endpoint
        try {
          console.log("Trying endpoint: https://aapharmasyn.com/admin/api/seo/pages/" + seoData.get?.('page_id') || seoData.page_id);

          if (isForm) {
            response = await externalApi.post(`https://aapharmasyn.com/admin/api/seo/pages/${seoData.get('page_id')}`, seoData, postOptions);
          } else {
            response = await externalApi.post(`https://aapharmasyn.com/admin/api/seo/pages/${seoData.page_id}`, {
              seo_title: seoData.seo_title,
              meta_description: seoData.meta_description,
              keywords: seoData.keywords,
              og_title: seoData.og_title,
              og_description: seoData.og_description,
              og_image: seoData.og_image,
              canonical_url: seoData.canonical_url,
              meta_robots: seoData.meta_robots,
              structured_data: seoData.structured_data
            });
          }
          console.log("SEO save successful:", response.data);
        } catch (apiError) {
          console.log("Main API endpoint failed:", apiError.message);
          console.log("Error status:", apiError.response?.status);
          console.log("Error data:", apiError.response?.data);
      
          // Try alternative endpoint structure
          try {
            console.log("Trying alternative endpoint: https://aapharmasyn.com/api/seo/pages/" + (isForm ? seoData.get('page_id') : seoData.page_id));

            if (isForm) {
              response = await externalApi.post(`https://aapharmasyn.com/api/seo/pages/${seoData.get('page_id')}`, seoData, postOptions);
            } else {
              response = await externalApi.post(`https://aapharmasyn.com/api/seo/pages/${seoData.page_id}`, {
                seo_title: seoData.seo_title,
                meta_description: seoData.meta_description,
                keywords: seoData.keywords,
                og_title: seoData.og_title,
                og_description: seoData.og_description,
                og_image: seoData.og_image,
                canonical_url: seoData.canonical_url,
                meta_robots: seoData.meta_robots,
                structured_data: seoData.structured_data
              });
            }
            console.log("Alternative API endpoint successful:", response.data);
          } catch (altError) {
            console.log("Alternative API endpoint failed:", altError.message);
            console.log("Alt error status:", altError.response?.status);
            console.log("Alt error data:", altError.response?.data);
        
            // Try one more endpoint variation
            try {
              console.log("Trying third endpoint: https://aapharmasyn.com/admin/api/seo/save");

              if (isForm) {
                // ensure page_id exists in form
                if (!seoData.get('page_id') && seoData.get('page_id') !== 0) {
                  // nothing to do - assume caller appended page_id
                }
                response = await externalApi.post(`https://aapharmasyn.com/admin/api/seo/save`, seoData, postOptions);
              } else {
                response = await externalApi.post(`https://aapharmasyn.com/admin/api/seo/save`, {
                  page_id: seoData.page_id,
                  seo_title: seoData.seo_title,
                  meta_description: seoData.meta_description,
                  keywords: seoData.keywords,
                  og_title: seoData.og_title,
                  og_description: seoData.og_description,
                  og_image: seoData.og_image,
                  canonical_url: seoData.canonical_url,
                  meta_robots: seoData.meta_robots,
                  structured_data: seoData.structured_data
                });
              }
              console.log("Third API endpoint successful:", response.data);
            } catch (thirdError) {
              console.log("Third API endpoint failed:", thirdError.message);
              throw new Error(`Failed to save SEO data. Tried multiple endpoints. Last error: ${thirdError.message}`);
            }
          }
        }
        
        return response.data;
      } catch (error) {
        console.error("Error saving SEO data:", error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Determine page id whether variables is FormData or plain object
      const isForm = typeof FormData !== 'undefined' && variables instanceof FormData;
      const pageId = isForm ? variables.get('page_id') : variables.page_id;

      // Invalidate and refetch related queries
      queryClient.invalidateQueries(["pages-seo"]);
      queryClient.invalidateQueries(["page-seo", pageId]);
      
      // Update the cache directly for immediate UI update
      queryClient.setQueryData(["page-seo", pageId], data);
    },
  });
};

// Delete SEO data for a page
export const DeletePageSEO = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (pageId) => {
      const response = await externalApi.delete(`https://aapharmasyn.com/admin/api/seo/pages/${pageId}`);
      return response.data;
    },
    onSuccess: (data, pageId) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries(["pages-seo"]);
      queryClient.invalidateQueries(["page-seo", pageId]);
    },
  });
};

// Bulk update SEO data for multiple pages
export const BulkUpdateSEO = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (bulkData) => {
      const response = await externalApi.post("https://aapharmasyn.com/admin/api/seo/bulk-update", bulkData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all SEO-related queries
      queryClient.invalidateQueries(["pages-seo"]);
      queryClient.invalidateQueries(["page-seo"]);
    },
  });
};

// Get SEO analytics and suggestions
export const GetSEOAnalytics = (timeRange = '30d') => {
  return useQuery({
    queryKey: ["seo-analytics", timeRange],
    queryFn: async () => {
      try {
        console.log("Fetching SEO analytics for time range:", timeRange);
        
        let response;
        
        // Try different API endpoint variations
        try {
          console.log("Trying endpoint: https://aapharmasyn.com/admin/api/seo/analytics?time_range=" + timeRange);
          response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/analytics?time_range=${timeRange}`);
          console.log("SEO analytics fetch successful:", response.data);
        } catch (apiError) {
          console.log("Main API endpoint failed:", apiError.message);
          
          try {
            console.log("Trying alternative endpoint: https://aapharmasyn.com/api/seo/analytics?time_range=" + timeRange);
            response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/analytics?time_range=${timeRange}`);
            console.log("Alternative API endpoint successful:", response.data);
          } catch (altError) {
            console.log("Alternative API endpoint failed:", altError.message);
            
            // Try one more endpoint variation
            try {
              console.log("Trying third endpoint: https://aapharmasyn.com/admin/api/seo/dashboard?time_range=" + timeRange);
              response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/dashboard?time_range=${timeRange}`);
              console.log("Third API endpoint successful:", response.data);
            } catch (thirdError) {
              console.log("Third API endpoint failed:", thirdError.message);
              throw new Error(`Failed to fetch SEO analytics. Tried multiple endpoints. Last error: ${thirdError.message}`);
            }
          }
        }
        
        return response.data;
      } catch (error) {
        console.error("Error fetching SEO analytics:", error);
        throw error;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
};

// Get SEO suggestions for a page
export const GetSEOSuggestions = (pageId) => {
  return useQuery({
    queryKey: ["seo-suggestions", pageId],
    queryFn: async () => {
      const response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/suggestions/${pageId}`);
      return response.data;
    },
    enabled: !!pageId,
  });
};

// Export SEO data
export const ExportSEOData = () => {
  return useMutation({
    mutationFn: async (format = "json") => {
      const response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/export?format=${format}`, {
        responseType: "blob",
      });
      return response.data;
    },
  });
};

// Import SEO data
export const ImportSEOData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      const response = await externalApi.post("https://aapharmasyn.com/admin/api/seo/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all SEO-related queries
      queryClient.invalidateQueries(["pages-seo"]);
      queryClient.invalidateQueries(["page-seo"]);
    },
  });
};

// Get SEO data by URL for frontend client
export const GetPageSEOByURL = () => {
  return useQuery({
    queryKey: ["page-seo-by-url"],
    queryFn: async (url) => {
      try {
        console.log("Fetching SEO data by URL:", url);
        
        let response;
        
        // Try different API endpoint variations
        try {
          console.log("Trying endpoint: https://aapharmasyn.com/admin/api/seo/pages/url/" + encodeURIComponent(url));
          response = await externalApi.get(`https://aapharmasyn.com/admin/api/seo/pages/url/${encodeURIComponent(url)}`);
          console.log("SEO fetch by URL successful:", response.data);
        } catch (apiError) {
          console.log("Main API endpoint failed:", apiError.message);
          
          try {
            console.log("Trying alternative endpoint: https://aapharmasyn.com/api/seo/pages/url/" + encodeURIComponent(url));
            response = await externalApi.get(`https://aapharmasyn.com/api/seo/pages/url/${encodeURIComponent(url)}`);
            console.log("Alternative API endpoint successful:", response.data);
          } catch (altError) {
            console.log("Alternative API endpoint failed:", altError.message);
            
            // Try one more endpoint variation
            try {
              console.log("Trying third endpoint: https://aapharmasyn.com/admin/api/seo/page-by-url");
              response = await externalApi.post(`https://aapharmasyn.com/admin/api/seo/page-by-url`, {
                url: url
              });
              console.log("Third API endpoint successful:", response.data);
            } catch (thirdError) {
              console.log("Third API endpoint failed:", thirdError.message);
              throw new Error(`Failed to fetch SEO data by URL. Tried multiple endpoints. Last error: ${thirdError.message}`);
            }
          }
        }
        
        return response.data;
      } catch (error) {
        console.error("Error fetching SEO data by URL:", error);
        throw error;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
};
